import React, { useEffect } from "react";
import { Button, Card, Form, Input, notification } from "antd";
import { useHistory, useLocation, useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useSelector } from "react-redux";
import { Breadcrumbs, Typography } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import styled from "styled-components";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { useMutate } from "hooks/useMutate";
import { getUserList } from "redux/selectors";
import COLORS from "common/Colors";

const labelStyle = {
    width: "80px",
    display: "inline-block",
    textAlign: "start",
};

const BillingEmail = () => {
    const { id: matterId } = useParams();
    const history = useHistory();
    const userList = useSelector(getUserList);
    const location = useLocation();
    const passedMatterData = location?.state?.matterData;
    const matterData = passedMatterData;

    const { getClient } = queryKeys;

    const { data: clientDataRes } = useFetch(
        getClient(matterData?.clientId).key,
        getClient(matterData?.clientId).url,
        {
            enabled: !!matterData?.clientId,
        }
    );

    const clientData = clientDataRes?.response?.data;

    const breadStyle = { color: "#636980", cursor: "pointer" };
    const breadcrumbs = [
        <label key="1" style={breadStyle} onClick={() => history.goBack()}>
            {matterData?.jobTitle || "Matter"}
        </label>,
        <Typography key="2" color="text.primary">
            Sending Bill
        </Typography>,
    ];

    return (
        <BillingWrap>
            <div className="title-row">
                <Breadcrumbs
                    separator={<NavigateNextIcon fontSize="small" />}
                    aria-label="breadcrumb"
                >
                    {breadcrumbs}
                </Breadcrumbs>

                <div className="client">
                    Client: {matterData?.clientId || "-"}
                </div>
            </div>

            <BillingEmailForm
                matterId={matterId}
                matterData={passedMatterData}
                userList={userList}
                clientData={clientData}
            />
        </BillingWrap>
    );
};

export default BillingEmail;

const BillingEmailForm = ({ matterId, matterData, userList, clientData }) => {
    const history = useHistory();
    const [form] = Form.useForm();

    const { key, url } = queryKeys.postBillingEmail;
    const { mutate: addBillingEmail, isLoading } = useMutate(key, url);

    const findUser = (id) => userList?.find((u) => u?.userid === id);

    const assignedPartner = findUser(matterData?.leadAttorney);

    const leadAttorneyName = assignedPartner
        ? `${assignedPartner.firstname} ${assignedPartner.lastname}`
        : null;

    useEffect(() => {
        if (matterData) {
            form.setFieldsValue({
                title: matterData?.jobTitle,
                subject: `Bill for ${matterData?.jobTitle} (${matterData?.matterId})`,
                to: [...new Set([clientData?.primarycontactemail, matterData?.responsiblePersonnel?.map((p) => p.emailid)?.join(", ")]
                    ?.filter(Boolean))]
                    ?.join(", "),
                footer: `Regards,\n${leadAttorneyName},\nMatter Lead Attorney.`,
            });
        }
    }, [matterData, clientData]);

    const handleSubmit = (values) => {
        const payload = {
            title: values.title,
            subject: values.subject,
            to: values.to,
            cc: values.cc,
            body: values.body,
            primarycontactperson: clientData?.primarycontactperson,
            footer: values.footer,
        };
        addBillingEmail(payload, {
            onSuccess: (res) => {
                notification.success({ message: res?.response?.message || "Email sent successfully" });
                history.push(`/matter-details/${matterId}`);
            },
        });
    };

    return (
        <Form
            form={form}
            onFinish={handleSubmit}
            layout="horizontal"
            colon={false}
        >
            <Card bordered={false}>
                <Form.Item
                    label={<span style={labelStyle}>Title:</span>}
                    name="title"
                    rules={[{ required: true, message: "Title is required" }]}
                >
                    <Input placeholder="Enter" disabled />
                </Form.Item>

                <Form.Item
                    label={<span style={labelStyle}>Subject:</span>}
                    name="subject"
                    rules={[{ required: true, message: "Subject is required" }]}
                >
                    <Input disabled placeholder="Enter" />
                </Form.Item>

                <Form.Item
                    label={<span style={labelStyle}>To:</span>}
                    name="to"
                    rules={[{ required: true, message: "Recipient email is required" }]}
                >
                    <Input disabled placeholder="Enter" />
                </Form.Item>

                <Form.Item
                    label={<span style={labelStyle}>CC:</span>}
                    name="cc"
                >
                    <Input placeholder="Enter" />
                </Form.Item>

                <Form.Item
                    label={<span style={labelStyle}>Body:</span>}
                    name="body"
                    rules={[{ required: true, message: "Body is required" }]}
                >
                    <Input.TextArea rows={3} style={{ resize: "none" }} />
                </Form.Item>

                <Form.Item
                    label={<span style={labelStyle}>Footer:</span>}
                    name="footer"
                    rules={[{ required: true, message: "Footer is required" }]}
                >
                    <Input.TextArea disabled rows={3} style={{ resize: "none" }} />
                </Form.Item>
            </Card>

            <div className="form-buttons">

                <Button
                    className="btn-color-close"
                    // type="primary"
                    onClick={() => history.push(`/matter-details/${matterId}`)}
                >
                    Close
                </Button>
                <Button
                    className="btn-color"
                    // type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    disabled={matterData?.Billingstatus}
                >
                    Send Mail
                </Button>
            </div>
        </Form>
    );
};

const BillingWrap = styled.div`
  .title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 16px;

    .client {
      font-size: 16px;
      color: #1b1b1b;
      font-weight: 600;
    }
  }

  label {
    color: #1b1b1b;
  }

  .form-buttons {
    margin-top: 10px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    .btn-color-close{
       background-color: rgb(255, 255, 255) !important;  
       color: #000000 !important; 
       border-color: #000000 !important;      
    }
    .btn-color{
       background-color: #00685F !important;  
       color: #ffffff !important;       
    }
    
  }

  .ant-card-body {
    box-shadow: 0px 3px 6px #00000015;
  }

  .ant-radio-group {
    display: flex;
    align-items: center;
    gap: 20px;

    label.ant-radio-button-wrapper {
      width: 100px;
      text-align: center;
      background-color: #f7f7f8;

      &:hover {
        box-shadow: #00000015 0px 3px 6px;
      }

      .ant-radio-button-checked {
        background-color: ${COLORS.PRIMARY};
        color: black !important;
      }

      color: black !important;
      border: none;
    }

    .ant-radio-button-wrapper:not(:first-child)::before {
      display: none;
    }
  }
`;